export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 16;
export const USERNAME_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9_.-]{1,14}[A-Za-z0-9])$/;

export type PlatformCategory = "console" | "pc" | "sandbox" | "streaming";

export type PlatformId =
  | "steam"
  | "steam-group"
  | "xbox"
  | "playstation"
  | "minecraft-java"
  | "minecraft-bedrock"
  | "roblox"
  | "epic"
  | "riot"
  | "twitch";

export interface PlatformDefinition {
  id: PlatformId;
  name: string;
  shortName: string;
  category: PlatformCategory;
  description: string;
  requiresSecret: boolean;
  supportsLiveCheck?: boolean;
  profileUrlTemplate?: string;
}

export type PlatformIntegrationStatus = "public_live" | "configured" | "missing_secret" | "reserved";

export interface PlatformIntegration {
  platform: PlatformId;
  name: string;
  category: PlatformCategory;
  requiresSecret: boolean;
  requiredSecrets: string[];
  missingSecrets: string[];
  ready: boolean;
  status: PlatformIntegrationStatus;
  note?: string;
}

export interface IntegrationsResponseData {
  checkedAt: string;
  summary: {
    total: number;
    ready: number;
    missingSecrets: number;
    reserved: number;
  };
  platforms: PlatformIntegration[];
  ai: {
    deepseek: boolean;
    aiGateway: boolean;
    generationReady: boolean;
  };
  turnstile: {
    enforcementEnabled: boolean;
    secretConfigured: boolean;
  };
}

export const PLATFORMS = [
  {
    id: "steam",
    name: "Steam",
    shortName: "ST",
    category: "pc",
    description: "Steam vanity profile URL",
    requiresSecret: true,
    profileUrlTemplate: "https://steamcommunity.com/id/{username}"
  },
  {
    id: "steam-group",
    name: "Steam Group",
    shortName: "SG",
    category: "pc",
    description: "Steam community group vanity URL",
    requiresSecret: true,
    profileUrlTemplate: "https://steamcommunity.com/groups/{username}"
  },
  {
    id: "xbox",
    name: "Xbox Live",
    shortName: "XB",
    category: "console",
    description: "Xbox gamertag profile",
    requiresSecret: true
  },
  {
    id: "playstation",
    name: "PlayStation",
    shortName: "PS",
    category: "console",
    description: "PlayStation Network online ID",
    requiresSecret: true
  },
  {
    id: "minecraft-java",
    name: "Minecraft Java",
    shortName: "MJ",
    category: "pc",
    description: "Mojang Java edition username",
    requiresSecret: false
  },
  {
    id: "minecraft-bedrock",
    name: "Minecraft Bedrock",
    shortName: "MB",
    category: "sandbox",
    description: "Bedrock identity through Xbox Live",
    requiresSecret: true
  },
  {
    id: "roblox",
    name: "Roblox",
    shortName: "RB",
    category: "sandbox",
    description: "Roblox username lookup",
    requiresSecret: false,
    profileUrlTemplate: "https://www.roblox.com/search/users?keyword={username}"
  },
  {
    id: "epic",
    name: "Epic Games",
    shortName: "EG",
    category: "pc",
    description: "Epic/Fortnite display name",
    requiresSecret: true
  },
  {
    id: "riot",
    name: "Riot Games",
    shortName: "RG",
    category: "pc",
    description: "Riot ID game name with default tag",
    requiresSecret: true
  },
  {
    id: "twitch",
    name: "Twitch",
    shortName: "TW",
    category: "streaming",
    description: "Twitch channel login",
    requiresSecret: true,
    profileUrlTemplate: "https://www.twitch.tv/{username}"
  }
] as const satisfies readonly PlatformDefinition[];

export const PLATFORM_IDS = PLATFORMS.map((platform) => platform.id) as PlatformId[];

export type AvailabilityValue = boolean | null;

export interface PlatformCheckResult {
  platform: PlatformId;
  name: string;
  category: PlatformCategory;
  available: AvailabilityValue;
  profileUrl: string | null;
  checkedAt: string;
  responseTime: number;
  cached: boolean;
  error?: string;
  reason?: "live" | "cache" | "configuration_required" | "timeout" | "unsupported" | "rate_limited" | "api_error";
}

export interface CheckSummary {
  total: number;
  available: number;
  unavailable: number;
  unknown: number;
}

export interface CheckResponseData {
  username: string;
  checkedAt: string;
  results: PlatformCheckResult[];
  summary: CheckSummary;
}

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export interface ApiFailure {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

export const GENERATOR_STYLE_IDS = ["dark", "sci-fi", "cute", "bold", "funny", "minimal"] as const;
export const GAME_TYPE_IDS = ["fps", "moba", "rpg", "battle-royale", "sandbox"] as const;
export type GenerationStyle = "dark" | "sci-fi" | "cute" | "bold" | "funny" | "minimal";
export type GameType = "fps" | "moba" | "rpg" | "battle-royale" | "sandbox";
export type LanguageCode = "en" | "zh-CN" | "zh-TW" | "ja" | "ko" | "ar";

export interface NameSuggestion {
  username: string;
  style: GenerationStyle;
  score: number;
  source: "ai-gateway" | "deepseek-direct" | "template-fallback";
}

export interface GenerateResponseData {
  suggestions: NameSuggestion[];
  generatedAt: string;
  cacheHit: boolean;
  provider: "ai-gateway" | "deepseek-direct" | "template-fallback";
}

export const GENERATION_STYLES: { id: GenerationStyle; label: string; description: string }[] = [
  { id: "dark", label: "Dark", description: "Shadowy, sharp, high-threat names." },
  { id: "sci-fi", label: "Sci-fi", description: "Neon, orbital, cybernetic names." },
  { id: "cute", label: "Cute", description: "Soft, playful, mascot-like names." },
  { id: "bold", label: "Bold", description: "Confident names for competitive players." },
  { id: "funny", label: "Funny", description: "Readable names with light chaos." },
  { id: "minimal", label: "Minimal", description: "Short, clean, brandable tags." }
];

export const GAME_TYPES: { id: GameType; label: string }[] = [
  { id: "fps", label: "FPS" },
  { id: "moba", label: "MOBA" },
  { id: "rpg", label: "RPG" },
  { id: "battle-royale", label: "Battle royale" },
  { id: "sandbox", label: "Sandbox" }
];

export function isPlatformId(value: string): value is PlatformId {
  return PLATFORM_IDS.includes(value as PlatformId);
}

export function getPlatform(id: PlatformId): PlatformDefinition {
  const platform = PLATFORMS.find((candidate) => candidate.id === id);
  if (!platform) {
    throw new Error(`Unknown platform: ${id}`);
  }
  return platform;
}

export function buildProfileUrl(platform: PlatformDefinition, username: string): string | null {
  if (!platform.profileUrlTemplate) {
    return null;
  }
  return platform.profileUrlTemplate.replace("{username}", encodeURIComponent(username));
}

export function validateUsername(username: string): { valid: true } | { valid: false; message: string } {
  const normalized = username.trim();
  if (!normalized) {
    return { valid: false, message: "Enter a gamer tag to check." };
  }
  if (normalized.length < USERNAME_MIN_LENGTH || normalized.length > USERNAME_MAX_LENGTH) {
    return { valid: false, message: "Use 3-16 characters." };
  }
  if (!USERNAME_PATTERN.test(normalized)) {
    return { valid: false, message: "Use letters, numbers, dots, dashes, or underscores. Start and end with a letter or number." };
  }
  return { valid: true };
}

export function isValidUsername(username: string): boolean {
  return validateUsername(username).valid;
}

export function buildSummary(results: readonly PlatformCheckResult[]): CheckSummary {
  return results.reduce<CheckSummary>(
    (summary, result) => {
      summary.total += 1;
      if (result.available === true) {
        summary.available += 1;
      } else if (result.available === false) {
        summary.unavailable += 1;
      } else {
        summary.unknown += 1;
      }
      return summary;
    },
    { total: 0, available: 0, unavailable: 0, unknown: 0 }
  );
}

export function normalizeUsername(username: string): string {
  return username.trim();
}
